/**
 * Parsed shape of an Ignition tag path. Tag paths arrive as
 * `[gateway]Project/Equipment/Signal[/extra/segments]`. Operators think
 * in equipment + signal, not raw paths — this helper extracts both.
 */
export interface ParsedTagPath {
  full: string
  /** First non-prefix segment, e.g. `CAP-401`. Null when the path can't
   *  be parsed into equipment + signal (degenerate / single-segment). */
  equipmentId: string | null
  /** Trailing portion after the equipment segment, e.g. `CapsApplied` or
   *  `Submeter/Phase_A` for nested signals. Falls back to the stripped
   *  path when no equipment segment exists. */
  signal: string
}

const BRACKET_PREFIX_RE = /^\[[^\]]*\]/
const URN_WRAPPER_SEGMENT_RE = /^[a-z]+:[^/]+\//
const URN_TYPE_PREFIX_RE = /^[a-z]+:/

/**
 * Normalize the many wire formats tag paths arrive in into a single
 * decoded slash-separated string suitable for splitting into segments.
 *
 * Shapes seen in real data:
 *   `[default]Plant_.../CAP-401/CapsApplied`                            (RCA, Ignition path with gateway bracket)
 *   `Plant_.../CAP-401/CapsApplied`                                     (RCA, no bracket)
 *   `urn:compute:default-gateway/tp:default/tag:Plant_%2FCAP-401%2FMode` (PM, full ontology URN — wrapper + URL-encoded leaf)
 *   `tp:default/tag:Plant_%2FCAP-401%2FMode`                            (PM, no wrapper)
 *   `tag:Plant_%2FCAP-401%2FMode`                                       (URN without tenant prefix)
 *   `equip:CAP-401`                                                     (URN pointing at an equipment node directly)
 */
function normalizeRawPath(raw: string): string {
  let s = raw.replace(BRACKET_PREFIX_RE, '')
  // Greedily strip leading wrapper segments shaped `<scheme>:<authority>/`
  // — covers `urn:compute:default-gateway/`, `tp:default/`, etc. Each
  // such segment is a routing marker, not part of the operator-facing
  // path. We don't know which prefixes are wrappers vs. real content,
  // so we keep peeling while the prefix matches the scheme pattern.
  let prev: string
  do {
    prev = s
    s = s.replace(URN_WRAPPER_SEGMENT_RE, '')
  } while (s !== prev)
  // After the wrappers there may be one more URN-type prefix in front
  // of an URL-encoded leaf path: `tag:Plant_%2FCAP-401%2FMode`.
  s = s.replace(URN_TYPE_PREFIX_RE, '')
  // The remainder may be URL-encoded (`%2F` for `/`). Decode so we can
  // split into segments downstream.
  try {
    s = decodeURIComponent(s)
  } catch {
    // malformed encoding — fall back to the partially-stripped string
  }
  return s
}

/**
 * Parse a tag path into equipment + signal. Handles all the shapes
 * documented in `normalizeRawPath`. For 3+ segments after normalization
 * the first is treated as the project (dropped); for 2 segments the
 * first is the equipment. For 1 segment we have only a signal and no
 * equipment can be inferred.
 */
export function parseTagPath(path: string): ParsedTagPath {
  const normalized = normalizeRawPath(path)
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length >= 3) {
    return {
      full: path,
      equipmentId: segments[1],
      signal: segments.slice(2).join('/')
    }
  }
  if (segments.length === 2) {
    return {
      full: path,
      equipmentId: segments[0],
      signal: segments[1]
    }
  }
  return { full: path, equipmentId: null, signal: normalized || path }
}

/**
 * Render a tag path as a compact "Equipment/Signal" label, dropping the
 * project / gateway prefix. Used in failure-mode contributing-tag chips
 * and anywhere a full path would dominate visually.
 */
export function formatTagShort(path: string): string {
  const parsed = parseTagPath(path)
  if (parsed.equipmentId) return `${parsed.equipmentId}/${parsed.signal}`
  return parsed.signal
}

export interface EquipmentGroup {
  equipmentId: string | null
  paths: ParsedTagPath[]
}

export function groupTagPathsByEquipment(paths: string[]): EquipmentGroup[] {
  const groups = new Map<string, ParsedTagPath[]>()
  const order: string[] = []
  for (const path of paths) {
    const parsed = parseTagPath(path)
    const key = parsed.equipmentId ?? '__unknown__'
    if (!groups.has(key)) {
      groups.set(key, [])
      order.push(key)
    }
    groups.get(key)!.push(parsed)
  }
  return order.map((key) => ({
    equipmentId: key === '__unknown__' ? null : key,
    paths: groups.get(key)!
  }))
}

/**
 * Extract the unique set of equipment ids referenced by a config's tag
 * paths, preserving first-seen order. Used by list cards to render a
 * compact "what equipment is this watching" chip strip.
 */
export function equipmentIdsFromTagPaths(paths: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const path of paths) {
    const parsed = parseTagPath(path)
    if (parsed.equipmentId && !seen.has(parsed.equipmentId)) {
      seen.add(parsed.equipmentId)
      out.push(parsed.equipmentId)
    }
  }
  return out
}
